import { CJEDTEvent } from '@teetsh/app/src/types/event';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { ClassroomsOrg } from '@teetsh/app/src/types/school';
import noOverlap from '@teetsh/app/src/components/EDT/rbc/no-overlap';
import { Row } from '@teetsh/app/src/containers/EmploiDuTemps/Print/types';
import {
  getEventsHappeningDuringTheSlice,
  getSlices,
} from '@teetsh/app/src/utils/slices';
import { getClassroomsGrouppedByDays } from './getClassroomsGrouppedByDays';
import { getLCMOfEvents } from './getLCMOfEvents';
import getDay from '@teetsh/app/src/utils/time/getDay';
import _zip from 'lodash-es/zip';
import { Slice } from '@teetsh/app/src/types/print';
import _floor from 'lodash-es/floor';
import filterEdtSeancesWithClassrooms from '@teetsh/app/src/components/EDT/filterEdtSeancesWithClassrooms';
import _minBy from 'lodash-es/minBy';
import { getSlotMetrics } from '@teetsh/app/src/components/EDT/rbc/TimeSlots';
import endOfDay from '@teetsh/app/src/utils/time/endOfDay';
import _maxBy from 'lodash-es/maxBy';
import { getRelatedEvents } from './getDistinctSlices';
import { roundTo } from './roundTo';
import _max from 'lodash-es/max';
import { RbcComputedEvent, CellPosition } from './types';
import { getEventsOfClassroomAndDay } from './getEventsOfClassroomAndDay';
import { getSliceLabel } from './getSliceLabel';
import { computeCells } from './computeCells';
import { addClassroomOrderToSeances } from '@teetsh/app/src/components/EDT/addClassroomOrderToSeances';
import { compactRows } from './compactRows';

interface PrecomputedSliceData {
  sliceTitle: string;
  eventsStarting: CJEDTEvent[];
  eventsStartingAndEnding: CJEDTEvent[];
  eventsHappeningDuring: CJEDTEvent[];
  eventsEnding: CJEDTEvent[];
  relatedEvents: CJEDTEvent[];
}

function precomputeSliceData(
  slices: Slice[],
  events: CJEDTEvent[],
): PrecomputedSliceData[] {
  return slices.map(slice => {
    const sliceTitle = getSliceLabel(slice);

    const {
      eventsStarting,
      eventsStartingAndEnding,
      eventsHappeningDuring,
      eventsEnding,
      allEvents,
    } = getEventsHappeningDuringTheSlice(events, slice);

    const relatedEvents =
      allEvents.length === 0 ? [] : getRelatedEvents(allEvents[0], events);

    return {
      sliceTitle,
      eventsStarting,
      eventsStartingAndEnding,
      eventsHappeningDuring,
      eventsEnding,
      relatedEvents,
    };
  });
}

function getUnitaryWidthAndPosition(
  e: RbcComputedEvent,
  columnWidth: number,
): CellPosition {
  const width = _max([_floor((e.widthInPixels * columnWidth) / 100), 1]) || 1;
  return {
    event: e.event,
    x: roundTo((e.leftInPixels * columnWidth) / 100, width),
    w: width,
  };
}

function rbcComputedEventsToCellPositions(
  events: CJEDTEvent[],
  rbcComputedEvents: RbcComputedEvent[],
  columnSize: number,
): CellPosition[] {
  return events.reduce<CellPosition[]>((acc, event) => {
    const matchingEvents = rbcComputedEvents.find(r => r.event.id === event.id);
    if (matchingEvents) {
      return [...acc, getUnitaryWidthAndPosition(matchingEvents, columnSize)];
    }
    return acc;
  }, []);
}

function computeRowForSingleDaySingleClassroom(
  classrooms: Classroom[],
  events: CJEDTEvent[],
  slices: Slice[],
  precomputedSliceData: PrecomputedSliceData[],
  day: number,
  size: number,
): Row[] {
  const classroomIds = classrooms.map(cl => cl.id);
  const eventsOfClassroom = filterEdtSeancesWithClassrooms(events, classrooms);

  const rbcComputedEvents: RbcComputedEvent[] =
    events.length > 0
      ? noOverlap({
          events: eventsOfClassroom,
          minimumStartDifference: 0,
          slotMetrics: getSlotMetrics({
            min: _minBy(events, e => e.start)?.start,
            max: endOfDay(_maxBy(events, e => e.end)?.end as Date),
            step: 1,
            timeslots: 200,
          }),
          accessors: {
            start: e => e.start,
            end: e => e.end,
          },
        }).map(e => ({
          event: e.event,
          leftInPixels: e.style.left,
          widthInPixels: e.style.width,
        }))
      : [];

  return slices.map((slice, sliceIndex) => {
    const precomputed = precomputedSliceData[sliceIndex];
    const {
      sliceTitle,
      eventsStarting,
      eventsStartingAndEnding,
      eventsHappeningDuring,
      eventsEnding,
      relatedEvents,
    } = precomputed;

    const allEventsStarting = [...eventsStarting, ...eventsStartingAndEnding];
    const allEventsStartingIds = allEventsStarting.map(e => e.id);
    const allEventsHappeningIds = [
      ...eventsHappeningDuring,
      ...eventsEnding,
    ].map(e => e.id);

    const otherEventsNotStarting = relatedEvents.filter(
      ev =>
        !allEventsStartingIds.includes(ev.id) &&
        !allEventsHappeningIds.includes(ev.id),
    );
    const eventsHappening = [...eventsHappeningDuring, ...eventsEnding];
    const otherEvents = otherEventsNotStarting;

    const eventsStartingOfTheDayOfTheSlice = rbcComputedEventsToCellPositions(
      getEventsOfClassroomAndDay(allEventsStarting, day, classroomIds),
      rbcComputedEvents,
      size,
    );

    const eventsHappeningOfTheDayOfTheSlice = rbcComputedEventsToCellPositions(
      getEventsOfClassroomAndDay(eventsHappening, day, classroomIds),
      rbcComputedEvents,
      size,
    );

    const eventsOthersOfTheDayOfTheSlice = rbcComputedEventsToCellPositions(
      getEventsOfClassroomAndDay(otherEvents, day, classroomIds),
      rbcComputedEvents,
      size,
    );
    const cells = computeCells(
      0,
      [],
      size,
      eventsStartingOfTheDayOfTheSlice,
      eventsHappeningOfTheDayOfTheSlice,
      eventsOthersOfTheDayOfTheSlice,
      slices,
    );

    return {
      title: sliceTitle,
      start: slice.start,
      end: slice.end,
      cells,
    };
  });
}

export function computeRowForSingleDay(
  classrooms: Classroom[],
  events: CJEDTEvent[],
  slices: Slice[],
  day: number,
  lcm: number,
  classroomsOrg: ClassroomsOrg,
): Row[] {
  // Precompute slice data once for all classrooms
  const precomputedSliceData = precomputeSliceData(slices, events);

  if (classroomsOrg === 'unified') {
    const rows = computeRowForSingleDaySingleClassroom(
      classrooms,
      addClassroomOrderToSeances(events, classrooms),
      slices,
      precomputedSliceData,
      day,
      lcm,
    );
    return compactRows(rows, lcm);
  }
  // remaining is here to handle the case where lcm > 1000 (which is the max width of a cell)
  // in this case, if there is 3 classrooms, the last one must have a width of 334
  const remaining = lcm % classrooms.length;
  return classrooms.reduce((acc, classroom, index) => {
    let size = _floor(lcm / classrooms.length);
    const isLast = index === classrooms.length - 1;
    if (isLast) {
      size = size + remaining;
    }
    const rows = computeRowForSingleDaySingleClassroom(
      [classroom],
      events,
      slices,
      precomputedSliceData,
      day,
      size,
    );
    const compactedRows = compactRows(rows, lcm);
    // const compactedRows = rows;
    if (acc.length !== 0) {
      const zipped = _zip(acc, compactedRows);
      return zipped.map(([first, second]: any) => ({
        ...first,
        cells: [...first.cells, ...second.cells],
      }));
    }
    return compactedRows;
  }, []);
}

export const computeTable = (
  events: CJEDTEvent[],
  classrooms: Classroom[],
  allDays: number[],
  classroomsOrg: ClassroomsOrg,
): Row[] => {
  const slices = getSlices(events);
  const groupedClassrooms = getClassroomsGrouppedByDays(allDays, classrooms);
  const lcm = getLCMOfEvents(events, classrooms, allDays, classroomsOrg);

  return allDays.reduce((acc, day) => {
    const classroomsOfTheDay = groupedClassrooms[day];
    const eventsOfTheDay = events.filter(e => getDay(e.start) === day);
    const rowsClassrooms = computeRowForSingleDay(
      classroomsOfTheDay,
      eventsOfTheDay,
      slices,
      day,
      lcm,
      classroomsOrg,
    );

    if (acc.length !== 0) {
      const zipped = _zip(acc, rowsClassrooms);
      return zipped.map(([first, second]: any) => ({
        ...first,
        cells: [...first.cells, ...second.cells],
      }));
    }
    return rowsClassrooms;
  }, []);
};
